import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Switch } from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import ReactDOM from "react-dom";
import { PrivateRoute, LoggedInRoute } from "./layouts/ProtectedRoutes";
import { createTheme, Snackbar, ThemeProvider } from "@material-ui/core";
import { customTheme } from "./theme";
import { FallbackComponent } from "./layouts/FallbackComponent";

import "assets/plugins/nucleo/css/nucleo.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "assets/scss/argon-dashboard-react.scss";
import "assets/scss/custom_styles.scss";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "assets/css/dashboard-custom-components.css";
import { Alert } from "@material-ui/lab";

const AdminLayout = React.lazy(() => import("./layouts/AdminLayout"));
const AuthLayout = React.lazy(() => import("./layouts/AuthLayout"));

export const AppContext = React.createContext();

const theme = createTheme(customTheme);

const App = () => {
  const [loggedInState, setLoggedInState] = useState(localStorage.getItem("isLogIn") === "true");
  const [message, setAppMessage] = useState(false);
  const [salesView, setSalesView] = useState({
    type: "table",
    status: {
      all_leads: { pageIndex: 0, pageSize: 10 },
      new: { pageIndex: 0, pageSize: 10 },
      follow_up: { pageIndex: 0, pageSize: 10 },
      payment_link: { pageIndex: 0, pageSize: 10 },
      booked: { pageIndex: 0, pageSize: 10 },
      cancelled: { pageIndex: 0, pageSize: 10 },
    },
  });
  const [servicesView, setServicesView] = useState({
    type: "table",
    status: {
      all_leads: { pageIndex: 0, pageSize: 10 },
      open: { pageIndex: 0, pageSize: 10 },
      in_progress: { pageIndex: 0, pageSize: 10 },
      completed: { pageIndex: 0, pageSize: 10 },
      cancelled: { pageIndex: 0, pageSize: 10 },
    },
  });
  const [escalationsView, setEscalationsView] = useState({
    type: "table",
    status: {
      all_escalations: { pageIndex: 0, pageSize: 10 },
      refunds: { pageIndex: 0, pageSize: 10 },
      penalty: { pageIndex: 0, pageSize: 10 },
    },
  });
  const [estimateCreated, setEstimateCreated] = useState("");
  const [notCreated, setNotCreated] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [openNotify, setOpenNotify] = useState({ open: false, type: "success", message: "" });

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenNotify((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    const handleStorageChange = () => {
      const isLoggedIn = localStorage.getItem("isLogIn") === "true";
      setLoggedInState(isLoggedIn);
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    let timer = null;
    if (estimateCreated !== "") {
      let i = 1;
      timer = setInterval(() => {
        if (i > 5) {
          setEstimateCreated("");
          clearInterval(timer);
        }
        i++;
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [estimateCreated]);

  return (
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <AppContext.Provider
          value={{
            message,
            setMessage: setAppMessage,
            salesView,
            setSalesView,
            servicesView,
            setServicesView,
            escalationsView,
            setEscalationsView,
            estimateCreated,
            setEstimateCreated,
            notCreated,
            setNotCreated,
            errorMsg,
            setErrorMsg,
            openNotify,
            setOpenNotify,
          }}
        >
          <BrowserRouter>
            <Suspense fallback={<FallbackComponent />}>
              <Switch>
                <PrivateRoute
                  path="/admin"
                  component={AdminLayout}
                  loggedIn={loggedInState}
                />
                <LoggedInRoute
                  exact
                  path="/"
                  component={AuthLayout}
                  loggedIn={loggedInState}
                />
              </Switch>
            </Suspense>
          </BrowserRouter>
          <Snackbar
            open={openNotify.open}
            autoHideDuration={3000}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert variant="filled" elevation={6} severity={openNotify.type}>
              {openNotify.message}
            </Alert>
          </Snackbar>
        </AppContext.Provider>
      </Provider>
    </ThemeProvider>
  );
};

ReactDOM.render(<App />, document.getElementById("root"));